/**
 * masters.gql-map.ts — the PURE mapping layer between the B0.7' masters
 * graph shapes and the legacy REST types (B2.6'-fe-seam).
 *
 * Deliberately import-free of the axios/i18n stack (governance.gql-map
 * precedent) so the vitest matrix can exercise every derivation in the
 * plain node environment. The facades re-export nothing from here — they
 * consume the mappers internally; the seam tests import this module direct.
 *
 * Mapping stance (contract-leads-SDL, B1.3' precedent):
 * - fields the contract does not carry map to '' / null / [] (rendered '-'
 *   by the list-kit) rather than invented values; required BOOLEANS the
 *   contract omits take the domain-neutral default, each one commented.
 * - IDS (SETTLED at CU-1a — masters-freeze id-canon row): the graph `id`
 *   stays an OPAQUE global ID (B0.4' codec law — the FE never decodes);
 *   the masters SDL grew RAW legacy-id fields (Vendor.vendorId,
 *   Customer.customerId, PartyCategory.categoryId, StaffMember.userId —
 *   the satellites' contactId/bankAccountId precedent) and the mappers
 *   read THOSE. The CU-1a shadow harness forced the call: opaque ids
 *   mapped to 0 on the legacy wire, so the created-entity round-trips
 *   404'd (category detail) and satellite writes 422'd (wizard contact on
 *   vendor 0). `toLegacyNumericId` remains ONLY for refs whose graph id
 *   is a plain numeric string (CompanyReference, companyByTaxId role
 *   refs); non-numeric still maps to 0 (honest-gap, list-kit renders '-').
 */

import { pickBilingualName } from './governance.gql-map';
import type {
  CompanyByTaxIdCheckResult,
  GqlCategoryTag,
  GqlCategoryTypeInfo,
  GqlCompanyPrefill,
  GqlCompanyRef,
  GqlContactListNode,
  GqlCurrencyNode,
  GqlCustomerDetailNode,
  GqlCustomerListNode,
  GqlPartyBankAccountNode,
  GqlPartyCategoryDetailNode,
  GqlPartyCategoryListNode,
  GqlPartyCategoryWriteEchoNode,
  GqlPartyContactNode,
  GqlPersonRef,
  GqlVendorCreatedNode,
  GqlVendorDetailNode,
  GqlVendorListNode,
} from './masters.operations';
import type {
  CategoryInfo,
  ExistingCompanyData,
  InternalContact,
  PaginationMeta,
} from '@/types/api/common';
import type { CategoryDetail, CategoryListItem, CategoryType } from '@/types/api/category';
import type {
  CheckExistsResponse,
  CreateVendorRequest,
  VendorDetailResponse,
  VendorListItem,
} from '@/types/api/vendor';
import type { ContactResponse, CreateContactRequest } from '@/types/api/contact';
import type { BankAccountResponse, CreateBankAccountRequest } from '@/types/api/bank';
import type { Currency } from '@/types/api/currency';
import type {
  CompanyReference,
  CustomerCheckExistsResponse,
  CustomerDetailResponse,
  CustomerListItem,
  RelatedOrganisation,
} from '@/types/api/customer';
import type {
  MasterDataContactListItem,
  ToggleMasterDataContactResponse,
} from '@/types/api/masterDataContact';

export { pickBilingualName };

/**
 * Opaque global ID → legacy numeric id. Plain numeric strings pass through;
 * anything else maps to 0 (never decoded — see module header). One place to
 * change when the masters freeze settles the id divergence.
 */
export function toLegacyNumericId(id: string): number {
  return /^\d+$/.test(id) ? Number(id) : 0;
}

/**
 * Legacy page/pageSize envelope derived from a relay `totalCount`. The live
 * leg serves the FIRST page (cursor plumbing is CU-1 flip scope), so
 * `page` is always 1 here; `pageSize <= 0` keeps the "-1 = every row"
 * options contract (one page of everything).
 */
export function toLegacyPagination(totalCount: number, pageSize: number | undefined): PaginationMeta {
  // Mirror the mock paginate() envelope EXACTLY (CU-1a shadow parity): the
  // requested pageSize echoes verbatim — including -1 (the options contract);
  // pageSize <= 0 collapses to ONE page of every row.
  if (pageSize !== undefined && pageSize <= 0) {
    return { page: 1, pageSize, total: totalCount, totalPages: 1 };
  }
  const effective = pageSize === undefined ? totalCount : pageSize;
  return {
    page: 1,
    pageSize: effective,
    total: totalCount,
    totalPages: effective > 0 ? Math.max(Math.ceil(totalCount / effective), 1) : 1,
  };
}

/**
 * "-1 / <=0 pageSize = every row" options-contract window for the live leg
 * (relay `first` must be a positive Int). 500 comfortably covers the L4
 * fixture corpus; the exact all-rows semantics ride the CU-1 flip.
 */
export const MASTERS_ALL_ROWS_FIRST = 500;

/** `pageSize` → the contract-pinned `$first` variable. */
export function toFirstVariable(pageSize: number | undefined): number {
  return pageSize === undefined || pageSize <= 0 ? MASTERS_ALL_ROWS_FIRST : pageSize;
}

/**
 * INTERIM until the CU-1 flip (B2.7'): the live list legs execute the page
 * operation with the contract-pinned {$first, $after} ONLY — filter / search
 * / sort / page>1 variable plumbing needs the masters SDL input shapes,
 * which freeze with the B2' graph slices. This helper names every legacy
 * constraint the live leg drops so facades can WARN loudly instead of
 * silently serving unfiltered data to an early flipper. The L2 live leg
 * stays XFAIL until the flip, so no parity claim rides these reads.
 */
export function listConstraintsDroppedOnLive(
  params: object | undefined,
  plumbed: readonly string[] = []
): string[] {
  if (!params) return [];
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (key === 'pageSize') continue;
    if (plumbed.includes(key)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (key === 'page' && (value === 1 || value === '1')) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    dropped.push(key);
  }
  return dropped;
}

// ---------------------------------------------------------------------------
// CU-1a flip — legacy list params → SDL filter inputs
// ---------------------------------------------------------------------------

/** Legacy tri-state isActive (1=active / 2=inactive / 3=all, or a boolean) →
 * the SDL's optional boolean (omitted = all). */
export function toIsActiveFilter(value: boolean | number | undefined): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 1) return true;
  if (value === 2) return false;
  return undefined;
}

/** Legacy comma-separated id CSV → Int[] (undefined when empty). */
export function toCategoryIdsFilter(csv: string | undefined): number[] | undefined {
  if (!csv) return undefined;
  const ids = csv
    .split(',')
    .map((raw) => Number(raw.trim()))
    .filter((id) => Number.isFinite(id));
  return ids.length > 0 ? ids : undefined;
}

/** Prune undefined entries; undefined when nothing survives (omit the arg). */
function pruneFilter(filter: Record<string, unknown>): Record<string, unknown> | undefined {
  const entries = Object.entries(filter).filter(([, value]) => value !== undefined);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/** Keys the vendor list live leg genuinely plumbs (census exclusion list). */
export const VENDOR_LIST_PLUMBED_KEYS = ['search', 'categoryIds', 'isActive', 'country'] as const;

/** VendorListParams → VendorFilterInput ('country' = countryId-stateId-cityId). */
export function toVendorFilterInput(
  params: { search?: string; categoryIds?: string; isActive?: number; country?: string } | undefined
): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const [countryId, stateId, cityId] = (params.country ?? '')
    .split('-')
    .map((part) => (part.trim().length > 0 ? Number(part) : undefined));
  return pruneFilter({
    search: params.search || undefined,
    categoryIds: toCategoryIdsFilter(params.categoryIds),
    isActive: toIsActiveFilter(params.isActive),
    countryId,
    stateId,
    cityId,
  });
}

/** Keys the customer list live leg genuinely plumbs. */
export const CUSTOMER_LIST_PLUMBED_KEYS = ['search', 'categoryIds', 'isActive'] as const;

/** CustomerListParams → CustomerFilterInput (no country facet in the SDL). */
export function toCustomerFilterInput(
  params: { search?: string; categoryIds?: string; isActive?: number } | undefined
): Record<string, unknown> | undefined {
  if (!params) return undefined;
  return pruneFilter({
    search: params.search || undefined,
    categoryIds: toCategoryIdsFilter(params.categoryIds),
    isActive: toIsActiveFilter(params.isActive),
  });
}

/** Keys the category list live leg genuinely plumbs. */
export const CATEGORY_LIST_PLUMBED_KEYS = ['search', 'isActive', 'typeId'] as const;

/** CategoryListParams → PartyCategoryFilterInput (typeId 1=VENDOR, 2=CUSTOMER;
 * the legacy multi-valued typeIds CSV stays census-dropped — the SDL models a
 * single domain facet). */
export function toPartyCategoryFilterInput(
  params: { search?: string; isActive?: boolean | number; typeId?: number } | undefined
): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const type = params.typeId === 1 ? 'VENDOR' : params.typeId === 2 ? 'CUSTOMER' : undefined;
  return pruneFilter({
    search: params.search || undefined,
    isActive: toIsActiveFilter(params.isActive),
    type,
  });
}

/** Graph category tag → legacy CategoryInfo (no color in the contract → omitted). */
export function mapCategoryTag(tag: GqlCategoryTag): CategoryInfo {
  return {
    categoryId: tag.categoryId,
    category: pickBilingualName(tag.nameEn, tag.nameZh),
  };
}

/** Graph person ref → legacy InternalContact (contract carries name only). */
export function mapInternalContact(ref: GqlPersonRef | null): InternalContact | null {
  if (!ref) return null;
  return {
    userId: ref.userId ?? 0,
    username: '',
    fullName: ref.fullName,
    email: '',
  };
}

export function mapCompanyReference(ref: GqlCompanyRef): CompanyReference {
  return {
    companyId: toLegacyNumericId(ref.id),
    companyName: ref.companyName,
  };
}

/** Compose the display phone from parts (the list row's single phone cell). */
export function composePhone(
  countryCode: string | null,
  phoneNumber: string | null,
  extension?: string | null
): string {
  const core = [countryCode, phoneNumber].filter(Boolean).join(' ');
  return extension ? `${core} ext ${extension}` : core;
}

// ---------------------------------------------------------------------------
// Vendor
// ---------------------------------------------------------------------------

/**
 * The LEGACY 9-key vendor list row EXACTLY (CU-1a read-leg parity — key
 * census from mocks/data/vendors/list.ts + the mock create handler):
 * vendorId, vendorName, individual, categories (FULL embedded objects),
 * abn, address/phone (entered-at-the-time display cells), internalContact
 * (null throughout the corpus), isActive. NO alternativeName / nickname /
 * updatedAt keys — the legacy wire never carried them.
 */
export function mapVendorListNode(node: GqlVendorListNode): VendorListItem {
  return {
    vendorId: node.vendorId,
    vendorName: node.companyName,
    individual: node.individual,
    categories: node.categories.map((tag) => ({
      categoryId: tag.categoryId,
      category: pickBilingualName(tag.nameEn, tag.nameZh),
      typeInfo: tag.typeInfo,
      description: tag.description ?? '',
      color: tag.color ?? '',
      isActive: tag.isActive,
    })),
    abn: node.localTaxId ?? '',
    address: node.listAddress ?? '',
    phone: node.listPhone ?? '',
    internalContact: null, // null throughout the legacy corpus
    isActive: node.isActive,
  };
}

export function mapVendorDetailNode(node: GqlVendorDetailNode): VendorDetailResponse {
  return {
    vendorId: node.vendorId,
    name: node.companyName,
    alternativeName: node.alternativeName,
    nickname: node.nickname,
    vendorCode: node.vendorCode ?? '',
    localTaxId: node.localTaxId,
    individual: node.individual,
    vendorType: node.individual ? 'individual' : 'company',
    categories: node.categories.map((tag) => ({
      categoryId: tag.categoryId,
      category: pickBilingualName(tag.nameEn, tag.nameZh),
      color: tag.color ?? '',
    })),
    primaryPort: node.primaryPort,
    salesRepresentativeIds: node.salesRepresentatives.map((rep) => rep.userId ?? 0),
    internalContact: mapInternalContact(node.internalContact),
    phoneCountryCode: node.phoneCountryCode,
    phoneNumber: node.phoneNumber,
    phoneExtension: node.phoneExtension,
    mobile: node.mobile,
    email: node.email,
    fax: node.fax,
    // Legacy dead field: every mock detail row (corpus AND created) serves
    // phone: null — the trio carries the real data (CU-1a shadow parity).
    phone: null,
    web: node.web,
    notes: node.notes,
    countryId: node.countryId ?? 0,
    stateId: node.registeredAddress?.stateId ?? 0,
    cityId: node.registeredAddress?.cityId ?? 0,
    postcode: node.registeredAddress?.postcode ?? null,
    address: node.registeredAddress?.address ?? null,
    // Key-presence parity: the legacy mock vendor detail/create rows carry
    // NO addressLang key at all — spread only when the graph has a value.
    ...(node.addressLang != null ? { addressLang: node.addressLang } : {}),
    isActive: node.isActive,
  };
}

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

/** The LEGACY customer list row EXACTLY (mock list fixture keys — slim tags,
 * customerCode, entered-at-the-time display cells; no alternativeName /
 * nickname / updatedAt keys). */
export function mapCustomerListNode(node: GqlCustomerListNode): CustomerListItem {
  return {
    customerId: node.customerId,
    customerName: node.companyName,
    customerCode: node.customerCode ?? '',
    individual: node.individual,
    categories: node.categories.map(mapCategoryTag),
    localTaxId: node.localTaxId ?? '',
    address: node.listAddress ?? '',
    phoneNumber: node.listPhone ?? '',
    internalContact: mapInternalContact(node.internalContact),
    isActive: node.isActive,
  };
}

export function mapCustomerDetailNode(node: GqlCustomerDetailNode): CustomerDetailResponse {
  return {
    customerId: node.customerId,
    name: node.companyName,
    alternativeName: node.alternativeName,
    nickname: null, // not carried by CustomerDetailFields
    individual: false, // not carried — company is the domain-neutral default
    customerCode: '', // not carried by CustomerDetailFields (renders '-')
    localTaxId: node.localTaxId ?? '',
    categories: node.categories.map(mapCategoryTag),
    internalContact: mapInternalContact(node.internalContact),
    parentCompany: node.parentCompany ? mapCompanyReference(node.parentCompany) : null,
    defaultLocation: null, // not carried by CustomerDetailFields
    subsidiaries: node.subsidiaries.map(mapCompanyReference),
    relatedOrganisations: node.relatedOrganisations.map(
      (ref): RelatedOrganisation => ({
        ...mapCompanyReference(ref),
        relationType: '', // not carried by CustomerDetailFields (renders '-')
      })
    ),
    isActiveOnlineAccount: false, // not carried — no-online-account is the neutral default
    email: node.email,
    fax: node.fax,
    phoneCountryCode: node.phoneCountryCode,
    phoneNumber: node.phoneNumber,
    phoneExtension: node.phoneExtension,
    web: node.web,
    accountNotes: node.accountNotes,
    deliveryNotes: node.deliveryNotes,
    salesNotes: node.salesNotes,
    countryId: node.countryId ?? 0,
    stateId: node.registeredAddress?.stateId ?? null,
    cityId: node.registeredAddress?.cityId ?? null,
    postcode: node.registeredAddress?.postcode ?? null,
    address: node.registeredAddress?.address ?? null,
    addressLang: node.addressLang,
    isActive: node.isActive,
  };
}

// ---------------------------------------------------------------------------
// Master-data contact
// ---------------------------------------------------------------------------

export function mapContactListNode(node: GqlContactListNode): MasterDataContactListItem {
  return {
    contactId: toLegacyNumericId(node.id),
    name: node.name,
    jobTitle: '', // not carried by ContactListRow (renders '-')
    companyName: node.linkedParties[0]?.companyName ?? '',
    phoneNumber: composePhone(node.phoneCountryCode, node.phoneNumber, node.phoneExtension),
    mobile: '', // not carried by ContactListRow (renders '-')
    fax: null,
    email: node.email ?? '',
    countryId: null,
    stateId: null,
    cityId: null,
    postcode: null,
    address: null,
    addressLang: null,
    isPrimary: false, // not carried by ContactListRow — non-primary is the neutral default
    isActive: node.isActive,
    // The linkedParties union selects no __typename, so the side is not
    // distinguishable from the wire — masters-freeze register candidate.
    vendorId: null,
    customerId: null,
    updatedAt: node.updatedAt ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Party category (flat)
// ---------------------------------------------------------------------------

function mapCategoryTypeInfo(info: GqlCategoryTypeInfo): CategoryType {
  return { typeId: info.typeId, type: info.type };
}

export function mapPartyCategoryListNode(node: GqlPartyCategoryListNode): CategoryListItem {
  // No createdAt/updatedAt: the legacy mock list rows carry NEITHER key
  // (corpus and created alike) — key-presence parity, CU-1a shadow run.
  return {
    categoryId: node.categoryId,
    category: pickBilingualName(node.nameEn, node.nameZh),
    typeInfo: node.typeInfo.map(mapCategoryTypeInfo),
    description: node.description ?? '',
    color: node.color ?? '',
    isActive: node.isActive,
  };
}

export function mapPartyCategoryDetailNode(node: GqlPartyCategoryDetailNode): CategoryDetail {
  return {
    categoryId: node.categoryId,
    category: pickBilingualName(node.nameEn, node.nameZh),
    typeInfo: node.typeInfo.map(mapCategoryTypeInfo),
    description: node.description ?? '',
    color: node.color ?? '',
    isActive: node.isActive,
  };
}

// ---------------------------------------------------------------------------
// CU-1a masters flip — write / dedupe / collection / reference mappers
// ---------------------------------------------------------------------------

/**
 * Legacy paginate() twin (mocks/handlers/utils.ts): pageSize <= 0 = one page
 * of every row (pageSize echoed as sent — the -1 options contract). Used by
 * the satellite collection live legs, which read a vendor-embedded list from
 * the graph and re-serve the legacy page envelope.
 */
export function paginateLegacy<T>(
  rows: readonly T[],
  page: number | undefined,
  pageSize: number | undefined
): { data: T[]; pagination: PaginationMeta } {
  const effectivePage = page ?? 1;
  const effectiveSize = pageSize ?? 10;
  const total = rows.length;
  if (effectiveSize <= 0) {
    return {
      data: [...rows],
      pagination: { page: effectivePage, pageSize: effectiveSize, total, totalPages: 1 },
    };
  }
  const start = (effectivePage - 1) * effectiveSize;
  return {
    data: rows.slice(start, start + effectiveSize),
    pagination: {
      page: effectivePage,
      pageSize: effectiveSize,
      total,
      totalPages: Math.max(Math.ceil(total / effectiveSize), 1),
    },
  };
}

/** CompanyPrefill → the legacy ExistingCompanyData pre-fill payload. */
export function mapCompanyPrefill(prefill: GqlCompanyPrefill): ExistingCompanyData {
  return {
    // External-registry docs carry no party id; the mock pins 0 (customer.ts
    // handler) and CompanyPrefill deliberately exposes none.
    companyId: 0,
    companyName: prefill.name,
    alternativeName: prefill.alternativeName ?? null,
    nickname: prefill.nickname ?? null,
    localTaxId: prefill.taxId ?? '',
    isIndividual: prefill.individual,
    email: prefill.email ?? null,
    fax: prefill.fax ?? null,
    phoneCountryCode: prefill.phoneCountryCode ?? null,
    phoneNumber: prefill.phoneNumber ?? null,
    phoneExtension: prefill.phoneExtension ?? null,
    web: prefill.web ?? null,
    countryId: prefill.countryId ?? 0,
    stateId: prefill.stateId ?? null,
    cityId: prefill.cityId ?? null,
    postcode: prefill.postcode ?? null,
    address: prefill.address ?? null,
    addressLang: prefill.addressLang ?? null,
  };
}

/**
 * companyByTaxId → the VENDOR wizard's CheckExistsResponse. Mock semantics
 * preserved: companyExists is only reported when no same-type duplicate
 * exists, and the vendorId/vendorName keys ride only on the exists branch.
 */
export function mapVendorCheckExists(result: CompanyByTaxIdCheckResult): CheckExistsResponse {
  const { vendor, company, message } = result.companyByTaxId;
  if (vendor) {
    return {
      exists: true,
      companyExists: false,
      vendorId: toLegacyNumericId(vendor.id),
      vendorName: vendor.companyName,
      message,
    };
  }
  if (company) {
    return { exists: false, companyExists: true, message, company: mapCompanyPrefill(company) };
  }
  return { exists: false, companyExists: false, message };
}

/** companyByTaxId → the CUSTOMER wizard's CustomerCheckExistsResponse. */
export function mapCustomerCheckExists(
  result: CompanyByTaxIdCheckResult
): CustomerCheckExistsResponse {
  const { customer, company, message } = result.companyByTaxId;
  if (customer) {
    return {
      exists: true,
      companyExists: false,
      customerId: toLegacyNumericId(customer.id),
      message,
    };
  }
  if (company) {
    return { exists: false, companyExists: true, message, company: mapCompanyPrefill(company) };
  }
  return { exists: false, companyExists: false, message };
}

/** CreateVendorRequest → VendorCreateInput variables (SDL field subset). */
export function toVendorCreateInput(data: CreateVendorRequest): Record<string, unknown> {
  return {
    name: data.name,
    alternativeName: data.alternativeName,
    nickname: data.nickname,
    // vendorCode is NOT in VendorCreateInput: identity is server-minted (the
    // mock mints VEN-nnn, the server draws the TRN sequence — reconciliation
    // is a masters-freeze register row; the legacy field is dropped here).
    localTaxId: data.localTaxId,
    individual: data.individual,
    categoryIds: data.categoryIds,
    primaryPort: data.primaryPort,
    salesRepresentativeIds: data.salesRepresentativeIds,
    internalContactUserId:
      data.internalContactUserId !== undefined ? String(data.internalContactUserId) : undefined,
    phoneCountryCode: data.phoneCountryCode,
    phoneNumber: data.phoneNumber,
    phoneExtension: data.phoneExtension,
    mobile: data.mobile,
    email: data.email,
    fax: data.fax,
    web: data.web,
    notes: data.notes,
    countryId: data.countryId,
    stateId: data.stateId,
    cityId: data.cityId,
    postcode: data.postcode,
    address: data.address,
    addressLang: data.addressLang,
    isActive: data.isActive,
  };
}

/**
 * Created-vendor node → the legacy POST /api/vendors 201 echo
 * (VendorDetailResponse). `legacyPhone` threads the request's composed
 * `phone` field through — the mock echoes `body.phone || null` and the graph
 * models phone as parts, so the echo semantic lives with the caller.
 */
export function mapVendorCreatedNode(
  node: GqlVendorCreatedNode,
  legacyPhone: string | null
): VendorDetailResponse {
  return {
    vendorId: node.vendorId,
    name: node.companyName,
    alternativeName: node.alternativeName,
    nickname: node.nickname,
    vendorCode: node.vendorCode ?? '',
    localTaxId: node.localTaxId,
    individual: node.individual,
    vendorType: node.individual ? 'individual' : 'company',
    categories: node.categories.map((tag) => ({
      categoryId: tag.categoryId,
      category: pickBilingualName(tag.nameEn, tag.nameZh),
      color: tag.color ?? '',
    })),
    primaryPort: node.primaryPort,
    salesRepresentativeIds: node.salesRepresentatives.map((rep) => rep.userId ?? 0),
    internalContact: mapInternalContact(node.internalContact),
    phoneCountryCode: node.phoneCountryCode,
    phoneNumber: node.phoneNumber,
    phoneExtension: node.phoneExtension,
    mobile: node.mobile,
    email: node.email,
    fax: node.fax,
    phone: legacyPhone,
    web: node.web,
    notes: node.notes,
    countryId: node.countryId ?? 0,
    stateId: node.registeredAddress?.stateId ?? 0,
    cityId: node.registeredAddress?.cityId ?? 0,
    postcode: node.registeredAddress?.postcode ?? null,
    address: node.registeredAddress?.address ?? null,
    // Key-presence parity: the legacy mock vendor detail/create rows carry
    // NO addressLang key at all — spread only when the graph has a value.
    ...(node.addressLang != null ? { addressLang: node.addressLang } : {}),
    isActive: node.isActive,
  };
}

/** CreateContactRequest → VendorContactAddInput variables. */
export function toVendorContactAddInput(
  vendorId: number,
  data: Omit<CreateContactRequest, 'vendorId'>
): Record<string, unknown> {
  return {
    vendorId: String(vendorId),
    name: data.name,
    jobTitle: data.jobTitle,
    isPrimary: data.isPrimary,
    email: data.email,
    fax: data.fax,
    phoneCountryCode: data.phoneCountryCode,
    phoneNumber: data.phoneNumber,
    phoneExtension: data.phoneExtension,
    mobileCountryCode: data.mobileCountryCode,
    mobileNumber: data.mobileNumber,
    countryId: data.countryId,
    stateId: data.stateId,
    cityId: data.cityId,
    postcode: data.postcode,
    address: data.address,
    addressLang: data.addressLang,
    isActive: data.isActive,
  };
}

/**
 * PartyContact node → legacy ContactResponse. Echo semantics mirror the mock
 * create handler: required-ish strings default '', optionals null; the
 * country/state/city display objects are not modeled by the graph (the
 * legacy wire serves null on creates — resolved names are a detail-page
 * concern the CU-1 flip's remaining leg carries).
 */
export function mapPartyContactNode(node: GqlPartyContactNode): ContactResponse {
  return {
    contactId: node.contactId,
    isPrimary: node.isPrimary,
    name: node.name,
    jobTitle: node.jobTitle ?? '',
    phoneCountryCode: node.phoneCountryCode ?? '',
    phoneNumber: node.phoneNumber ?? '',
    phoneExtension: node.phoneExtension ?? null,
    mobileCountryCode: node.mobileCountryCode ?? null,
    mobileNumber: node.mobileNumber ?? null,
    fax: node.fax ?? null,
    email: node.email ?? '',
    countryId: node.countryId ?? null,
    stateId: node.stateId ?? null,
    cityId: node.cityId ?? null,
    country: null,
    state: null,
    city: null,
    postcode: node.postcode ?? null,
    address: node.address ?? null,
    addressLang: node.addressLang ?? null,
    isActive: node.isActive,
  };
}

/**
 * PartyContact node → the master-data contact TOGGLE echo.
 *
 * The same graph node `mapPartyContactNode` maps, projected onto the OTHER
 * legacy shape: `masterDataContactApi`'s two toggles declare
 * `ToggleMasterDataContactResponse` while `contactApi.toggleStatus` declares
 * `ContactResponse`. Both address the same party-scoped route and ride the
 * same document — only the projection differs, which is why the operation
 * selects the superset and each facade narrows here rather than at the wire.
 *
 * `companyName` is null and that is FAITHFUL, not a gap: `PartyContact` does
 * not carry it and the party-scoped mock handler
 * (`mocks/handlers/vendor.ts`) echoes a contact fixture that has no such key
 * either — so neither seam ever served it.
 */
export function mapPartyContactToggleEcho(
  node: GqlPartyContactNode
): ToggleMasterDataContactResponse {
  return {
    contactId: node.contactId,
    isPrimary: node.isPrimary,
    name: node.name,
    jobTitle: node.jobTitle ?? '',
    companyName: null,
    phoneCountryCode: node.phoneCountryCode ?? '',
    phoneNumber: node.phoneNumber ?? '',
    phoneExtension: node.phoneExtension ?? null,
    mobileCountryCode: node.mobileCountryCode ?? null,
    mobileNumber: node.mobileNumber ?? null,
    fax: node.fax ?? null,
    email: node.email ?? '',
    address: node.address ?? null,
    isActive: node.isActive,
  };
}

/** CreateBankAccountRequest → VendorBankAccountAddInput variables. */
export function toVendorBankAccountAddInput(
  vendorId: number,
  data: Omit<CreateBankAccountRequest, 'vendorId'>
): Record<string, unknown> {
  return {
    vendorId: String(vendorId),
    accountName: data.accountName,
    accountNumber: data.accountNumber,
    currency: data.currency,
    bankName: data.bankName,
    bankCode: data.bankCode,
    branchName: data.branchName,
    branchCode: data.branchCode,
    countryId: data.countryId,
    swiftCode: data.swiftCode,
    iban: data.iban,
    notes: data.notes,
    isDefault: data.isDefault,
    isActive: data.isActive,
  };
}

/**
 * PartyBankAccount node → legacy BankAccountResponse (legacy `bankId` = the
 * ACCOUNT id — bankAccountId; the reference-bank id never rode this wire).
 */
export function mapPartyBankAccountNode(node: GqlPartyBankAccountNode): BankAccountResponse {
  return {
    // Wire bankId = the legacy corpus row key when seeded, else the account
    // PK (created rows) — the same projection the server's write mutations
    // resolve (CU-1a bank id-canon; shadow-run-1 parity).
    bankId: node.bankId ?? node.bankAccountId,
    accountName: node.accountName,
    accountNumber: node.accountNumber,
    currency: node.currency ?? '',
    bankName: node.bankName ?? '',
    branchName: node.branchName ?? null,
    // Key-presence parity: the legacy mock create echo carries NO
    // bankCode/branchCode keys, while corpus rows carry values — spread
    // conditionally so absent stays absent (deepDiff distinguishes).
    ...(node.bankCode != null ? { bankCode: node.bankCode } : {}),
    ...(node.branchCode != null ? { branchCode: node.branchCode } : {}),
    countryId: node.countryId ?? 0,
    countryNameEn: node.countryNameEn ?? '',
    countryNameZh: node.countryNameZh ?? '',
    swiftCode: node.swiftCode ?? null,
    iban: node.iban ?? null,
    notes: node.notes ?? null,
    isDefault: node.isDefault,
    isActive: node.isActive,
  };
}

/**
 * PartyCategory write echo → the legacy transformCategory shape (create /
 * toggle responses). createdAt/updatedAt keys mirror transformCategory:
 * present-but-undefined (the legacy mock write echo carries neither).
 */
export function mapPartyCategoryWriteEcho(node: GqlPartyCategoryWriteEchoNode): CategoryDetail {
  return {
    categoryId: node.categoryId,
    category: pickBilingualName(node.nameEn, node.nameZh),
    typeInfo: node.typeInfo.map(mapCategoryTypeInfo),
    description: node.description ?? '',
    color: node.color ?? '',
    isActive: node.isActive,
  };
}

/**
 * Currency node → legacy Currency row. The legacy wire carries a numeric
 * currencyId the graph deliberately does not model (code IS the identity);
 * ids are re-derived positionally against the platform corpus order — the
 * same 1-based order the mock corpus pins.
 */
export function mapCurrencyNode(node: GqlCurrencyNode, index: number): Currency {
  return {
    currencyId: index + 1,
    currencyCode: node.code,
    currencyNameEn: node.nameEn,
    currencyNameZh: node.nameZh,
    currencySymbol: node.symbol,
  };
}
